/**
 * Service Worker Manager
 *
 * Manages SW lifecycle to ensure users always get the latest code.
 * When a new SW is detected:
 * 1. Wait for it to install
 * 2. Tell it to activate (skipWaiting)
 * 3. Reload the page when it takes control
 *
 * Features:
 * - History tracking in localStorage for debugging
 * - Debug overlay when ?debug is present
 * - Only enabled when localPlayback is on
 */

import { debugOverlay } from './components/Player/debugOverlay'

const log = (...args: unknown[]) => console.log('[SW Manager]', ...args)

const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000 // 15 minutes
const HISTORY_STORAGE_KEY = '__sw_history'
const MAX_HISTORY_ITEMS = 50
const RELOAD_DELAY_MS = 2000
const BACKUP_RELOAD_MS = 30000

interface HistoryEvent {
  type: string
  data?: unknown
  timestamp: string
}

class ServiceWorkerManager {
  private static instance: ServiceWorkerManager
  private registration: ServiceWorkerRegistration | null = null
  private reloading = false
  private updateInterval: number | null = null
  private history: HistoryEvent[] = []

  private constructor() {
    this.handleControllerChange = this.handleControllerChange.bind(this)
    this.handleUpdateFound = this.handleUpdateFound.bind(this)
    this.loadHistory()
  }

  static getInstance(): ServiceWorkerManager {
    if (!ServiceWorkerManager.instance) {
      ServiceWorkerManager.instance = new ServiceWorkerManager()
    }
    return ServiceWorkerManager.instance
  }

  // --- History Management ---

  private loadHistory(): void {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY)
      if (stored) {
        this.history = JSON.parse(stored)
      }
    } catch (e) {
      log('Failed to load history:', e)
    }
  }

  private saveHistory(): void {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(this.history))
    } catch (e) {
      log('Failed to save history:', e)
    }
  }

  private addEvent(type: string, data?: unknown): void {
    const event: HistoryEvent = {
      type,
      data,
      timestamp: new Date().toISOString(),
    }
    this.history.push(event)
    // Keep only last N items
    if (this.history.length > MAX_HISTORY_ITEMS) {
      this.history = this.history.slice(-MAX_HISTORY_ITEMS)
    }
    this.saveHistory()
    log(type, data ?? '')
  }

  getHistory(): HistoryEvent[] {
    return [...this.history]
  }

  clearHistory(): void {
    this.history = []
    localStorage.removeItem(HISTORY_STORAGE_KEY)
  }

  // --- SW Lifecycle ---

  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      this.addEvent('not-supported')
      return
    }

    // Listen for controller changes (new SW took control)
    navigator.serviceWorker.addEventListener('controllerchange', this.handleControllerChange)

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js')
      this.addEvent('registered', { scope: this.registration.scope })
      debugOverlay.set('sw', 'Service Worker: ON')

      // Listen for new SW versions
      this.registration.addEventListener('updatefound', this.handleUpdateFound)

      // Check for updates periodically
      this.updateInterval = window.setInterval(() => {
        this.addEvent('update-check')
        this.registration?.update()
      }, UPDATE_CHECK_INTERVAL_MS)
    } catch (error) {
      this.addEvent('registration-failed', { error: String(error) })
    }
  }

  async unregister(): Promise<boolean> {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
      this.updateInterval = null
    }

    if (!this.registration) {
      // Try to get existing registration
      const reg = await navigator.serviceWorker.getRegistration()
      if (reg) {
        const success = await reg.unregister()
        this.addEvent('unregistered', { success })
        if (success) {
          debugOverlay.remove('sw')
        }
        return success
      }
      return false
    }

    const success = await this.registration.unregister()
    if (success) {
      this.registration = null
      this.addEvent('unregistered', { success: true })
      debugOverlay.remove('sw')
    }
    return success
  }

  private handleControllerChange(): void {
    this.addEvent('controllerchange')
    this.reload()
  }

  private handleUpdateFound(): void {
    const newWorker = this.registration?.installing
    if (!newWorker) return

    this.addEvent('updatefound')

    newWorker.addEventListener('statechange', () => {
      this.handleStateChange(newWorker)
    })
  }

  private handleStateChange(worker: ServiceWorker): void {
    this.addEvent('statechange', { state: worker.state })

    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      // New SW is installed and waiting, there's an existing controller
      // This means it's an UPDATE, not first install
      this.addEvent('new-version-ready')

      debugOverlay.set('sw-update', 'Service Worker: updating...')

      setTimeout(() => {
        this.activateWorker(worker)
      }, RELOAD_DELAY_MS)

      // Backup reload in case controllerchange doesn't fire
      setTimeout(() => {
        this.addEvent('backup-reload')
        this.reload()
      }, BACKUP_RELOAD_MS)
    }
  }

  private activateWorker(worker: ServiceWorker): void {
    this.addEvent('skip-waiting-sent')
    worker.postMessage({ type: 'SKIP_WAITING' })
  }

  private reload(): void {
    if (this.reloading) return
    this.reloading = true
    this.addEvent('reloading')
    debugOverlay.remove('sw-update')
    window.location.reload()
  }

  checkForUpdates(): void {
    this.addEvent('manual-update-check')
    this.registration?.update()
  }

  isRegistered(): boolean {
    return this.registration !== null
  }
}

export const swManager = ServiceWorkerManager.getInstance()

/**
 * Initialize SW based on localPlayback setting.
 * Call this when the screen config is loaded.
 */
export async function initServiceWorker(localPlaybackEnabled: boolean): Promise<void> {
  if (localPlaybackEnabled) {
    const isRegistered = await navigator.serviceWorker?.getRegistration()
    if (!isRegistered) {
      log('LocalPlayback enabled, registering SW...')
      await swManager.register()
    } else {
      debugOverlay.set('sw', 'Service Worker: ON')
    }
  } else {
    const isRegistered = await navigator.serviceWorker?.getRegistration()
    if (isRegistered) {
      log('LocalPlayback disabled, unregistering SW...')
      await swManager.unregister()
    }
  }
}
